import { Fragment, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ReactJson from "react-json-view";
import Drawer from "react-modern-drawer";
import {
    fetchJvByRestLink,
    fetchJvSlAccumulator,
    fetchJvSpecStopLossContractsByNames,
    fetchJvSpecStopLossContractsForClient,
    fetchReadJvClient,
    flattenReinsuranceTracker,
} from "../../utils/apiUtils";
import { camelCaseToWords } from "../../utils/commonUtils";
import StopLossContractDetail, { asJvArray } from "../benefitPlan/SharedTable/detailViews/StopLossContractDetail";

const pick = (...vals) => vals.find((v) => v != null && String(v).trim() !== "");

const sameName = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

const pad2 = (n) => String(n).padStart(2, "0");

const toIsoDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const toJvDate = (value) => {
    if (value == null || String(value).trim() === "") {
        return toIsoDate(new Date());
    }
    const s = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`;
    const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdy) return `${mdy[3]}-${pad2(mdy[1])}-${pad2(mdy[2])}`;
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return toIsoDate(d);
    return s;
};

const cardStyle = {
    backgroundColor: "var(--white)",
    borderRadius: ".6rem",
    boxShadow: "rgba(0, 0, 0, 0.05) 0px 0px 0px 1px",
    padding: "1rem 1.2rem",
    marginBottom: "1rem",
};

const statLabelStyle = {
    fontSize: ".72rem",
    color: "var(--grey)",
    textTransform: "uppercase",
    letterSpacing: ".03em",
    marginBottom: ".2rem",
};

export const jvMemberClientKeys = (member, searchRow) => {
    const maint = member?.EmployeeMaintenance || member?.DependentMaintenance || {};
    const coverage =
        asJvArray(member?.EmployeeCoverages?.EmpCoverageEffectiveDateRecord)[0]
        || asJvArray(member?.DependentCoverages?.DependentEligibilityCoverage)[0]
        || {};
    const eligibility = searchRow?.Eligibility || {};

    return {
        clientId: pick(
            searchRow?.ClientID,
            searchRow?.clientid,
            eligibility.ClientID,
            member?.ClientID,
            maint.ClientID,
            coverage.ClientID,
        ),
        blkOfBusId: pick(
            searchRow?.BlkOfBusID,
            searchRow?.blkofbusid,
            eligibility.BlkOfBusID,
            member?.BlkOfBusID,
            maint.BlkOfBusID,
            coverage.BlkOfBusID,
        ),
        location: pick(
            searchRow?.Location,
            eligibility.Locations,
            coverage.Location,
            maint.Location,
        ),
        department: pick(
            searchRow?.Department,
            eligibility.Department,
            coverage.Department,
            maint.Department,
        ),
        employeeId: pick(
            searchRow?.EmployeeID,
            member?.EmployeeID,
            maint.EmployeeID,
        ),
        certificateNumber: pick(
            searchRow?.CertificateNumber,
            maint.CertificateNumber,
            member?.CertificateNumber,
        ),
        dependentId: pick(
            searchRow?.DependentID,
            member?.DependentID,
            maint.DependentID,
        ),
        effectiveDate: pick(
            eligibility.EffectiveDate,
            coverage.EffectiveDate,
            searchRow?.EffectiveDate,
            maint.EffectiveDate,
            member?.EffectiveDate,
        ),
    };
};

export const assignedStopLossName = (client, { location, department } = {}) => {
    const locations = asJvArray(client?.ClientLocation);
    const locMatches = location
        ? locations.filter((loc) => sameName(loc?.LocationName, location))
        : [];
    const searchLocs = locMatches.length ? locMatches : locations;

    for (const loc of searchLocs) {
        for (const dept of asJvArray(loc?.ClientLocationDepartment)) {
            if (department && !sameName(dept?.DepartmentName, department)) continue;
            if (dept?.StopLossContract) return String(dept.StopLossContract).trim();
        }
    }

    for (const dept of asJvArray(client?.ClientDepartment)) {
        if (department && !sameName(dept?.DepartmentName, department)) continue;
        if (dept?.StopLossContract) return String(dept.StopLossContract).trim();
    }

    return client?.StopLossContract ? String(client.StopLossContract).trim() : null;
};

const ACCUMULATOR_RESPONSE_KEYS = [
    "SLAccumulator",
    "SlAccumulator",
    "StopLossAccumulator",
    "SpecStopLossAccumulator",
    "ReinsuranceTracker",
    "Accumulator",
];

const extractAccumulators = (payload) => {
    const response = payload?.Response || payload || {};
    for (const key of ACCUMULATOR_RESPONSE_KEYS) {
        const rows = asJvArray(response[key]);
        if (rows.length) return rows.map((row) => flattenReinsuranceTracker({ Response: row }));
    }
    const { MetaData, Pagination, ...rest } = response;
    const leftover = Object.keys(rest).filter((k) => rest[k] != null && rest[k] !== "");
    if (!leftover.length) return [];
    if (response.SummaryAndAmounts || response.StopLossContractName) {
        return [flattenReinsuranceTracker(payload)];
    }
    return asJvArray(rest).length ? asJvArray(rest) : [rest];
};

const SKIP_KEYS = new Set(["MetaData", "Pagination"]);

const IDENTITY_KEYS = [
    "ClaimNumber",
    "ClaimID",
    "ClaimNo",
    "EmployeeID",
    "DependentID",
    "CertificateNumber",
    "TrackerID",
    "StopLossContract",
    "Name",
    "ID",
];

const COLUMN_PRIORITY = [
    ...IDENTITY_KEYS,
    "Status",
    "ClaimStatus",
    "PaidDate",
    "IncurredDate",
    "ServiceDate",
    "EffectiveDate",
    "PaidAmt",
    "BilledAmt",
    "AllowedAmt",
    "Amount",
];

const MAX_HYDRATE_DEPTH = 2;
const MAX_HYDRATE_FETCHES = 40;
const MAX_REPEAT_FETCHES = 25;
const HYDRATE_CONCURRENCY = 5;
const MAX_TABLE_COLS = 7;
const DRAWER_SIZE = 1100;

const isBlank = (value) => value == null || String(value).trim() === "";

const primitiveEntries = (row) =>
    Object.entries(row || {}).filter(([key, value]) => {
        if (SKIP_KEYS.has(key)) return false;
        return value == null || ["string", "number", "boolean"].includes(typeof value);
    });

const isJvLink = (key, value) => {
    if (typeof value !== "string" || !value.trim()) return false;
    if (/link$/i.test(String(key))) return true;
    return /\/rest\//i.test(value) || /^https?:\/\//i.test(value);
};

const LABEL_OVERRIDES = {
    "PercentageOfSpecDeductibleMetAsOf-PercentageMetDate": "% of spec met",
    PercentageMetDate: "As of",
    DisplayAgeBasedOn: "Age based on",
    TrackerAction: "Current view",
    SpecificAmount: "Spec deductible",
    SpecDeductibleAmt: "Spec deductible",
    AmountAppliedToSpecDeductible: "Applied to spec",
    TotalOfPaidClaimsIncurredOverTheContractPeriod: "Paid claims in contract period",
    TotalOfPaidClaimsIncurredOvertheContractPeriod: "Paid claims in contract period",
    WarningAmt: "Warning amount",
    WarningPercent: "Warning %",
    MaxPaidOutPerYearAmt: "Annual max",
    PaidAmt: "Paid",
    BilledAmt: "Billed",
    AllowedAmt: "Allowed",
    ClaimNumber: "Claim",
    ClaimID: "Claim",
    IncurredDate: "Incurred",
    PaidDate: "Paid on",
    ServiceDate: "Service",
    SubmissionID: "Submission",
    SubmissionDate: "Submitted",
    EmployeeID: "Employee",
    DependentID: "Dependent",
    CertificateNumber: "Certificate",
};

const AGE_BASED_ON = { C: "Certificate", E: "Employee", D: "Dependent", M: "Member" };

const NOISE_KEYS = new Set([
    "EmployeeID",
    "DependentID",
    "CertificateNumber",
    "ClientID",
    "BlkOfBusID",
    "Location",
    "Department",
    "NoRecordsFound",
    "RecordNotFoundMsg",
    "SearchResultsHeader",
]);

const PAID_KEYS = [
    "TotalOfPaidClaimsIncurredOverTheContractPeriod",
    "TotalOfPaidClaimsIncurredOvertheContractPeriod",
];
const APPLIED_KEYS = ["AmountAppliedToSpecDeductible"];
const DEDUCTIBLE_KEYS = ["SpecificAmount", "SpecDeductibleAmt"];
const PCT_KEYS = [
    "PercentageOfSpecDeductibleMetAsOf",
    "PercentageOfSpecDeductibleMetAsOf-PercentageMetDate",
    "PercentageMetDate",
];
const SPEC_STORY_KEYS = new Set([...PAID_KEYS, ...APPLIED_KEYS, ...DEDUCTIBLE_KEYS, ...PCT_KEYS]);

const VIEW_BLURBS = {
    summary: "Position against the specific deductible as of the tracker date.",
    carrier: "Claims the carrier is responsible for under this contract.",
    overmax: "Claims that went over the specific maximum.",
    reimbursed: "Claims already reimbursed by stop-loss.",
    unfiled: "Claims still in Pay & File.",
    outstanding: "Claims that can be reimbursed from the stop-loss fund.",
    submission: "Stop-loss filing batches.",
    tracker: "Member accumulator position.",
    claims: "Claims in this view.",
};

const CLAIM_COLUMNS = [
    "ClaimNumber", "ClaimID", "ClaimNo",
    "IncurredDate", "ServiceDate", "PaidDate",
    "BilledAmt", "AllowedAmt", "PaidAmt", "Amount",
    "Status", "ClaimStatus",
];

const SUBMISSION_COLUMNS = [
    "SubmissionID", "SubmissionNumber", "BatchID",
    "SubmissionDate", "PaidDate",
    "PaidAmt", "BilledAmt", "ClaimCount", "NumberOfClaims",
    "Status",
];

const COLUMNS_BY_GROUP = {
    summary: CLAIM_COLUMNS,
    carrier: CLAIM_COLUMNS,
    overmax: CLAIM_COLUMNS,
    reimbursed: CLAIM_COLUMNS,
    unfiled: CLAIM_COLUMNS,
    outstanding: CLAIM_COLUMNS,
    claims: CLAIM_COLUMNS,
    submission: SUBMISSION_COLUMNS,
};

const humanizeField = (key) => {
    if (LABEL_OVERRIDES[key]) return LABEL_OVERRIDES[key];
    const first = String(key || "").replace(/Link$/i, "").split("-")[0];
    return camelCaseToWords(first.replace(/[-_]/g, " "));
};

const objectIdentity = (obj) => {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return null;
    for (const key of IDENTITY_KEYS) {
        const value = obj[key];
        if (value == null || isJvLink(key, value)) continue;
        const text = String(value).trim();
        if (text) return text;
    }
    return null;
};

const collectJvLinks = (value, acc = [], seen = new Map(), parent = null, depth = 0) => {
    if (value == null) return acc;
    if (Array.isArray(value)) {
        value.forEach((item) => collectJvLinks(item, acc, seen, item, depth + 1));
        return acc;
    }
    if (typeof value !== "object") return acc;
    for (const [key, nested] of Object.entries(value)) {
        if (SKIP_KEYS.has(key)) continue;
        if (isJvLink(key, nested)) {
            const href = String(nested).trim();
            const existing = seen.get(href);
            if (existing) {
                if (!existing.aliases.includes(key) && key !== existing.label) {
                    existing.aliases.push(key);
                }
            } else {
                const record = {
                    label: key,
                    href,
                    aliases: [],
                    identity: objectIdentity(value) || objectIdentity(parent),
                    depth,
                };
                seen.set(href, record);
                acc.push(record);
            }
        } else if (nested && typeof nested === "object") {
            collectJvLinks(nested, acc, seen, value, depth + 1);
        }
    }
    return acc;
};

const normalizeHref = (href) => {
    const raw = String(href || "").trim();
    const restIndex = raw.toLowerCase().indexOf("/rest/");
    const path = restIndex >= 0
        ? raw.slice(restIndex + "/rest/".length)
        : raw.replace(/^https?:\/\/[^/]+\//i, "").replace(/^\//, "");
    return path.replace(/\/+$/, "").toLowerCase();
};

const linkPath = (href) => {
    try {
        const path = href.replace(/^https?:\/\/[^/]+/i, "");
        return path.toLowerCase().includes("/rest/")
            ? path.slice(path.toLowerCase().indexOf("/rest/") + "/rest/".length)
            : path.replace(/^\//, "");
    } catch {
        return href;
    }
};

const linkLabel = (href, fallback) => {
    const rest = linkPath(href);
    return rest.split("/").filter(Boolean).slice(0, 4).join("/") || fallback || "Open";
};

const splitLinks = (links) => {
    const counts = links.reduce((acc, link) => {
        acc[link.label] = (acc[link.label] || 0) + 1;
        return acc;
    }, {});
    const unique = [];
    const groups = new Map();
    for (const link of links) {
        if (counts[link.label] >= 3) {
            if (!groups.has(link.label)) groups.set(link.label, []);
            groups.get(link.label).push(link);
        } else {
            unique.push(link);
        }
    }
    return { unique, groups };
};

const VIEW_GROUP_DEFS = [
    { id: "summary", title: "Summaries", match: /summary/i, strip: /\b(summary|view)\b/gi },
    { id: "carrier", title: "Carrier responsible", match: /carrierresp|carrier.?resp/i, strip: /\b(carrier\s*resp(onsible)?|claims?|view)\b/gi },
    { id: "overmax", title: "Over maximum", match: /overmax|over.?max/i, strip: /\b(over\s*max(imum)?|claims?|view)\b/gi },
    { id: "unfiled", title: "Unfiled · Pay & File", match: /unfiled|pay.?&?.?file|marksasfiled/i, strip: /\b(unfiled|pay\s*&?\s*file|claims?|view)\b/gi },
    { id: "outstanding", title: "Outstanding · Stop-loss fund", match: /outstanding|stoplossfund|stop.?loss.?fund/i, strip: /\b(outstanding|stop\s*loss\s*fund|claims?|view)\b/gi },
    { id: "reimbursed", title: "Reimbursed", match: /reimburse/i, strip: /\b(reimburse[d]?|claims?|view)\b/gi },
    { id: "submission", title: "Submissions", match: /submission/i, strip: /\b(submission|view)\b/gi },
    { id: "tracker", title: "Trackers", match: /tracker/i, strip: /\b(tracker|view)\b/gi },
    { id: "claims", title: "Claims", match: /claim/i, strip: /\b(claims?|view)\b/gi },
];

const OTHER_VIEW_GROUP = { id: "other", title: "Other", strip: /\bview\b/gi };

const classifyViewGroup = (label) =>
    VIEW_GROUP_DEFS.find((group) => group.match.test(String(label || ""))) || OTHER_VIEW_GROUP;

const viewDisplayLabel = (label, group) => {
    const cleaned = String(label || "").replace(/Link$/i, "");
    const hyphenParts = cleaned.split("-").filter(Boolean);
    const seed = hyphenParts.length > 1 ? hyphenParts[hyphenParts.length - 1] : cleaned;
    let text = humanizeField(seed);
    if (group?.strip) {
        const stripped = text.replace(group.strip, " ").replace(/\s+/g, " ").trim();
        if (stripped.length >= 2) text = stripped;
        else if (/submission/i.test(cleaned)) text = "Submission";
        else if (/claim/i.test(cleaned)) text = "Claims";
    }
    return text;
};

const groupViews = (links) => {
    const buckets = new Map();
    for (const link of links || []) {
        const group = classifyViewGroup(link.label);
        if (!buckets.has(group.id)) {
            buckets.set(group.id, { id: group.id, title: group.title, items: [] });
        }
        buckets.get(group.id).items.push({
            ...link,
            displayLabel: viewDisplayLabel(link.label, group),
        });
    }
    const order = [...VIEW_GROUP_DEFS.map((group) => group.id), "other"];
    return [...buckets.values()].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
};

const unwrapPayload = (payload) => {
    if (payload == null) return {};
    const response = payload.Response != null ? payload.Response : payload;
    if (Array.isArray(response)) return response;
    if (!response || typeof response !== "object") return {};
    return flattenReinsuranceTracker({ Response: response });
};

const parseNumber = (value) => {
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    const n = Number(String(value).replace(/[$,%\s]/g, ""));
    return Number.isFinite(n) ? n : null;
};

const formatDate = (value) => {
    if (isBlank(value)) return "";
    const s = String(value).trim();
    const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    const d = ymd
        ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
        : mdy
            ? new Date(Number(mdy[3]), Number(mdy[1]) - 1, Number(mdy[2]))
            : new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatValue = (key, value) => {
    if (value == null || value === "") return "";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    const text = String(value).trim();
    const keyText = String(key || "");

    if (keyText === "DisplayAgeBasedOn") {
        return AGE_BASED_ON[text.toUpperCase()] || text;
    }
    if (keyText === "TrackerAction") {
        return humanizeField(text);
    }

    const yn = text.toUpperCase();
    if (["Y", "YES"].includes(yn)) return "Yes";
    if (["N", "NO"].includes(yn)) return "No";

    if (/date/i.test(keyText) || /^\d{4}-\d{2}-\d{2}/.test(text) || /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(text)) {
        return formatDate(text);
    }
    const n = parseNumber(text);
    if (n != null && /percent|pct|%/i.test(keyText)) {
        return `${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
    }
    if (n != null && isMoneyKey(keyText)) {
        return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
    }
    return text;
};

const isMoneyKey = (key) =>
    !/date/i.test(String(key)) && /amt|amount|billed|allowed|deductible|threshold|charge|reimburse|balance|fund|paid/i.test(String(key));

const objectCollections = (body) => {
    if (!body || typeof body !== "object" || Array.isArray(body)) return [];
    return Object.entries(body)
        .filter(([key]) => !SKIP_KEYS.has(key))
        .map(([key, value]) => {
            const rows = asJvArray(value).filter((item) => item && typeof item === "object" && !Array.isArray(item));
            return rows.length ? { key, rows } : null;
        })
        .filter(Boolean);
};

const flattenRecord = (payload) => {
    const acc = {};
    const walk = (obj, depth) => {
        if (obj == null || depth > 2) return;
        if (Array.isArray(obj)) {
            obj.forEach((item) => walk(item, depth));
            return;
        }
        if (typeof obj !== "object") return;
        for (const [key, value] of Object.entries(obj)) {
            if (SKIP_KEYS.has(key) || isJvLink(key, value)) continue;
            if (value && typeof value === "object") walk(value, depth + 1);
            else if (!isBlank(value) && acc[key] == null) acc[key] = value;
        }
    };
    walk(unwrapPayload(payload), 0);
    return acc;
};

const pickColumns = (rows, preferred = COLUMN_PRIORITY) => {
    const present = (key) => rows.some((row) => !isBlank(row?.[key]) && !isJvLink(key, row[key]));
    const preferredPresent = (preferred || []).filter(present);
    if (preferredPresent.length >= 3) return preferredPresent.slice(0, MAX_TABLE_COLS + 1);

    const filled = new Map();
    for (const row of rows) {
        for (const [key, value] of primitiveEntries(row)) {
            if (isJvLink(key, value) || isBlank(value) || NOISE_KEYS.has(key)) continue;
            filled.set(key, (filled.get(key) || 0) + 1);
        }
    }
    const extra = [...filled.keys()]
        .filter((key) => !preferredPresent.includes(key))
        .sort((a, b) => {
            const ai = COLUMN_PRIORITY.indexOf(a);
            const bi = COLUMN_PRIORITY.indexOf(b);
            if (ai !== -1 || bi !== -1) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
            return filled.get(b) - filled.get(a) || a.localeCompare(b);
        });
    return [...preferredPresent, ...extra].slice(0, MAX_TABLE_COLS + 1);
};

const tableTitle = (label) => {
    if (/claimtracker/i.test(label)) return "Trackers";
    if (/claim/i.test(label)) return "Claims";
    if (/submission/i.test(label)) return "Submissions";
    return humanizeField(label);
};

const partitionStats = (entries) => {
    const hero = [];
    const rest = [];
    for (const [key, value] of entries || []) {
        if (NOISE_KEYS.has(key) || SPEC_STORY_KEYS.has(key) || isJvLink(key, value) || isBlank(value)) continue;
        if (/percent|deductible|paidamt|remaining|maxpaid|warningamt|specpaid|reimburseamt|^total|specificamount|appliedtospec|attachmentpoint/i.test(key) && parseNumber(value) != null) {
            hero.push([key, value]);
        } else {
            rest.push([key, value]);
        }
    }
    return { hero: hero.slice(0, 4), rest };
};

const mapPool = async (items, limit, fn) => {
    const results = new Array(items.length);
    let index = 0;
    const worker = async () => {
        while (index < items.length) {
            const current = index++;
            results[current] = await fn(items[current], current);
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) || 0 }, worker));
    return results;
};

const linksToFollow = (payload, { depth, rootKey }) => {
    const links = collectJvLinks(unwrapPayload(payload));
    const { unique, groups } = splitLinks(links);
    const next = [];
    const seen = new Set();
    const push = (link) => {
        const key = normalizeHref(link.href);
        if (!key || key === rootKey || seen.has(key)) return;
        seen.add(key);
        next.push(link);
    };
    unique.forEach(push);
    if (depth === 0) {
        for (const items of groups.values()) {
            items.slice(0, MAX_REPEAT_FETCHES).forEach(push);
        }
    }
    return next;
};

const hydrateHref = async (link, ctx) => {
    const key = normalizeHref(link.href);
    const node = {
        label: link.label,
        href: link.href,
        identity: link.identity,
        payload: null,
        error: null,
        children: [],
    };

    if (!key) {
        node.error = "Missing link";
        return node;
    }

    if (ctx.cache.has(key)) {
        node.payload = ctx.cache.get(key);
    } else if (ctx.errors.has(key)) {
        node.error = ctx.errors.get(key);
        return node;
    } else if (ctx.counts.fetches >= MAX_HYDRATE_FETCHES) {
        node.error = "Too many linked records to load at once.";
        return node;
    } else {
        ctx.counts.fetches += 1;
        try {
            node.payload = await fetchJvByRestLink(link.href);
            ctx.cache.set(key, node.payload);
        } catch (err) {
            node.error = err?.message || "Unable to load linked record.";
            ctx.errors.set(key, node.error);
            return node;
        }
    }

    if (ctx.depth >= MAX_HYDRATE_DEPTH || ctx.path.has(key) || !node.payload) {
        return node;
    }

    ctx.path.add(key);
    const childLinks = linksToFollow(node.payload, { depth: ctx.depth, rootKey: ctx.rootKey });
    node.children = (await mapPool(childLinks, HYDRATE_CONCURRENCY, (child) => (
        hydrateHref(child, { ...ctx, depth: ctx.depth + 1 })
    ))).filter(Boolean);
    ctx.path.delete(key);
    return node;
};

const splitChildNodes = (children) => splitLinks(children || []);

const JvLinkButton = ({ label, href, onOpen, loading }) => (
    <span
        role="button"
        tabIndex={0}
        onClick={() => onOpen({ label, href })}
        onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpen({ label, href });
            }
        }}
        title={href}
        style={{
            color: "var(--darkerGrey)",
            cursor: "pointer",
            fontSize: ".88rem",
            textDecoration: "underline",
            textUnderlineOffset: "3px",
            textDecorationColor: "var(--lightGrey)",
        }}
    >
        {loading ? "Loading…" : (label || linkLabel(href, "View"))}
    </span>
);

const ViewGroups = ({ links, onOpen, loadingHref }) => {
    const grouped = groupViews(links);
    const hideTitles = grouped.length === 1 && grouped[0].id === "other";
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {grouped.map((group) => (
                <div key={group.id}>
                    {!hideTitles && (
                        <p style={{ ...statLabelStyle, paddingBottom: ".4rem" }}>{group.title}</p>
                    )}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem .9rem" }}>
                        {group.items.map((link) => (
                            <JvLinkButton
                                key={link.href}
                                label={hideTitles ? humanizeField(link.label) : link.displayLabel}
                                href={link.href}
                                onOpen={onOpen}
                                loading={loadingHref === link.href}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

const viewCardStyle = {
    ...cardStyle,
    marginBottom: 0,
    padding: "1rem",
    display: "flex",
    flexDirection: "column",
    gap: ".45rem",
    minHeight: "8.5rem",
};

const viewChipStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: ".6rem",
    width: "100%",
    textAlign: "left",
    padding: ".35rem 0",
    border: "none",
    borderBottom: "1px solid var(--lightestGrey)",
    background: "transparent",
    cursor: "pointer",
    fontSize: ".88rem",
    color: "var(--darkerGrey)",
};

const IDENTITY_CHIPS = [
    ["StopLossContractName", "Contract"],
    ["StopLossContractID", "Contract ID"],
    ["SpecStopLossContractID", "Contract ID"],
    ["ClientID", "Client"],
    ["ClientName", "Client name"],
    ["Client", "Client name"],
    ["BlkOfBusID", "Blob"],
    ["BlobID", "Blob"],
    ["LocationName", "Location"],
    ["EmployeeID", "Employee"],
    ["DependentID", "Dependent"],
    ["CertificateNumber", "Certificate"],
    ["EffectiveDate", "As of"],
    ["AggregateContractName", "Aggregate contract"],
    ["AggrContractID", "Aggregate ID"],
    ["SearchClmNumber", "Claim"],
    ["ClaimNumber", "Claim"],
];

const IDENTITY_KEY_SET = new Set(IDENTITY_CHIPS.map(([key]) => key));

const IdentityStrip = ({ row }) => {
    const seen = new Set();
    const parts = [];
    for (const [key, label] of IDENTITY_CHIPS) {
        const value = row?.[key];
        if (isBlank(value) && value !== 0) continue;
        if (seen.has(label)) continue;
        seen.add(label);
        parts.push(`${label} ${formatValue(label, value)}`);
    }
    if (!parts.length) return null;
    return (
        <p style={{ color: "var(--grey)", fontSize: ".85rem", paddingBottom: "1rem", lineHeight: 1.45 }}>
            {parts.join(" · ")}
        </p>
    );
};

const ViewGroupCards = ({ links, onOpen, loadingHref }) => {
    const [query, setQuery] = useState("");
    const grouped = groupViews(links);
    if (!grouped.length) return null;
    const needle = query.trim().toLowerCase();
    const visible = needle
        ? grouped
            .map((group) => ({
                ...group,
                items: group.items.filter((link) => (
                    `${group.title} ${link.displayLabel || ""} ${link.label || ""}`
                        .toLowerCase()
                        .includes(needle)
                )),
            }))
            .filter((group) => group.items.length)
        : grouped;
    const total = grouped.reduce((n, group) => n + group.items.length, 0);

    return (
        <div>
            {total > 6 && (
                <input
                    type="search"
                    value={query}
                    placeholder="Filter views…"
                    aria-label="Filter linked views"
                    onChange={(e) => setQuery(e.target.value)}
                    style={{
                        ...statLabelStyle,
                        textTransform: "none",
                        letterSpacing: 0,
                        fontSize: ".9rem",
                        color: "var(--darkerGrey)",
                        width: "100%",
                        maxWidth: "22rem",
                        marginBottom: "1rem",
                        padding: ".55rem .75rem",
                        borderRadius: ".4rem",
                        border: "1px solid var(--lightGrey)",
                        outline: "none",
                    }}
                />
            )}
            {!visible.length ? (
                <p style={{ color: "var(--grey)", fontSize: ".85rem" }}>No views match “{query}”.</p>
            ) : (
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                    gap: "1rem",
                }}>
                    {visible.map((group) => (
                        <div key={group.id} style={viewCardStyle}>
                            <div>
                                <p style={{ fontSize: ".95rem", fontWeight: 600, color: "var(--darkerGrey)", display: "flex", gap: ".45rem", alignItems: "center" }}>
                                    {group.title}
                                    <span style={{
                                        fontSize: ".72rem",
                                        fontWeight: 500,
                                        color: "var(--grey)",
                                        background: "var(--lightestGrey)",
                                        borderRadius: "1rem",
                                        padding: "0 .45rem",
                                    }}>
                                        {group.items.length}
                                    </span>
                                </p>
                                {VIEW_BLURBS[group.id] && (
                                    <p style={{ color: "var(--grey)", fontSize: ".78rem", paddingTop: ".25rem", lineHeight: 1.4 }}>
                                        {VIEW_BLURBS[group.id]}
                                    </p>
                                )}
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: ".35rem", marginTop: "auto" }}>
                                {group.items.map((link) => (
                                    <button
                                        key={link.href}
                                        type="button"
                                        style={viewChipStyle}
                                        title={link.href}
                                        onClick={() => onOpen({ label: link.label, href: link.href })}
                                    >
                                        <span>{link.displayLabel || humanizeField(link.label)}</span>
                                        <span style={{ flexShrink: 0, color: "var(--grey)", fontSize: ".75rem" }}>
                                            {loadingHref === link.href ? "…" : "View"}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const pickNumericField = (row, keys) => {
    for (const key of keys) {
        if (row?.[key] == null || row[key] === "") continue;
        const n = parseNumber(row[key]);
        if (n != null) return { key, n };
    }
    return null;
};

const money = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const SpecPosition = ({ row }) => {
    const paid = pickNumericField(row, PAID_KEYS);
    const applied = pickNumericField(row, APPLIED_KEYS);
    const deductible = pickNumericField(row, DEDUCTIBLE_KEYS);
    const pctField = pickNumericField(row, PCT_KEYS);
    if (!paid && !applied && !deductible) return null;
    const remaining = deductible && applied ? Math.max(0, deductible.n - applied.n) : null;
    const pct = deductible && deductible.n > 0 && applied
        ? Math.max(0, Math.min(100, (applied.n / deductible.n) * 100))
        : pctField?.n ?? null;
    const met = remaining === 0 && !!deductible;

    return (
        <div style={{ paddingBottom: "1.25rem" }}>
            <p style={{ fontSize: ".9rem", color: "var(--darkerGrey)", paddingBottom: ".9rem", lineHeight: 1.45, maxWidth: "36rem" }}>
                {met
                    ? "Specific stop-loss is met. Further qualifying claims are the carrier’s."
                    : remaining != null
                        ? `${money(remaining)} left before specific stop-loss applies.`
                        : "Position against the specific deductible."}
            </p>
            <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: "1rem 1.5rem",
                maxWidth: "40rem",
            }}>
                {paid && (
                    <div>
                        <div style={statLabelStyle}>Plan paid</div>
                        <div style={{ fontSize: "1.4rem", fontWeight: 650, letterSpacing: "-.02em" }}>{money(paid.n)}</div>
                        <div style={{ fontSize: ".75rem", color: "var(--grey)", paddingTop: ".15rem" }}>in the contract period</div>
                    </div>
                )}
                {applied && (
                    <div>
                        <div style={statLabelStyle}>Applied to spec</div>
                        <div style={{ fontSize: "1.4rem", fontWeight: 650, letterSpacing: "-.02em" }}>{money(applied.n)}</div>
                        {deductible && (
                            <div style={{ fontSize: ".75rem", color: "var(--grey)", paddingTop: ".15rem" }}>
                                of {money(deductible.n)} deductible
                            </div>
                        )}
                    </div>
                )}
                {remaining != null && (
                    <div>
                        <div style={statLabelStyle}>{met ? "Stop-loss" : "Left before spec"}</div>
                        <div style={{ fontSize: "1.4rem", fontWeight: 650, letterSpacing: "-.02em" }}>
                            {met ? "Met" : money(remaining)}
                        </div>
                    </div>
                )}
            </div>
            {pct != null && (
                <div style={{
                    marginTop: "1rem",
                    height: 4,
                    background: "var(--lightGrey)",
                    borderRadius: 99,
                    overflow: "hidden",
                    maxWidth: "28rem",
                }}>
                    <div style={{
                        width: `${pct}%`,
                        height: "100%",
                        background: "var(--darkerGrey)",
                    }} />
                </div>
            )}
        </div>
    );
};

const Fact = ({ fieldKey, label, value }) => {
    if (isBlank(value) && value !== 0) return null;
    return (
        <div>
            <div style={statLabelStyle}>{label}</div>
            <div style={{ fontSize: ".92rem", fontWeight: 600, color: "var(--darkerGrey)", wordBreak: "break-word" }}>
                {formatValue(fieldKey || label, value)}
            </div>
        </div>
    );
};

const FactGrid = ({ entries }) => {
    const rows = (entries || []).filter(([key, value]) => !NOISE_KEYS.has(key) && (!isBlank(value) || value === 0));
    if (!rows.length) return null;
    return (
        <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
            gap: ".85rem 1.1rem",
        }}>
            {rows.map(([key, value]) => (
                <Fact key={key} fieldKey={key} label={humanizeField(key)} value={value} />
            ))}
        </div>
    );
};

const KpiRow = ({ entries }) => {
    if (!entries?.length) return null;
    return <FactGrid entries={entries} />;
};

const Section = ({ title, count, children, defaultOpen = true }) => {
    const [open, setOpen] = useState(defaultOpen);
    if (children == null) return null;
    return (
        <div style={{ paddingTop: "1rem" }}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                    paddingBottom: open ? ".55rem" : 0,
                }}
                onClick={() => setOpen(!open)}
            >
                <span style={{ fontSize: ".85rem", fontWeight: 600, color: "var(--darkerGrey)", display: "flex", gap: ".45rem", alignItems: "center" }}>
                    {title}
                    {count != null && (
                        <span style={{
                            fontSize: ".72rem",
                            fontWeight: 500,
                            color: "var(--grey)",
                            background: "var(--lightestGrey)",
                            borderRadius: "1rem",
                            padding: "0 .45rem",
                        }}>
                            {count}
                        </span>
                    )}
                </span>
                <span style={{ fontSize: ".8rem", color: "var(--grey)" }}>{open ? "▾" : "▸"}</span>
            </div>
            {open && children}
        </div>
    );
};

const RecordTable = ({ rows, nestedByIndex, preferredColumns }) => {
    const [expanded, setExpanded] = useState(null);
    const columns = pickColumns(rows, preferredColumns);
    if (!columns.length) return null;

    const moneyCols = columns.filter((key) => isMoneyKey(key) && rows.some((row) => parseNumber(row[key]) != null));
    const totals = Object.fromEntries(moneyCols.map((key) => [
        key,
        rows.reduce((sum, row) => sum + (parseNumber(row[key]) || 0), 0),
    ]));

    return (
        <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr>
                        {columns.map((key) => (
                            <th
                                key={key}
                                style={{
                                    textAlign: isMoneyKey(key) ? "right" : "left",
                                    fontSize: ".72rem",
                                    fontWeight: 600,
                                    letterSpacing: ".03em",
                                    textTransform: "uppercase",
                                    color: "var(--grey)",
                                    padding: ".45rem .7rem .45rem 0",
                                    borderBottom: "1px solid var(--lightGrey)",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {humanizeField(key)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => {
                        const nested = nestedByIndex?.[i];
                        const leftover = primitiveEntries(row).filter(([key, value]) => (
                            !columns.includes(key)
                            && !NOISE_KEYS.has(key)
                            && !isJvLink(key, value)
                            && !isBlank(value)
                        ));
                        const tracker = nested?.children?.find((child) => /tracker/i.test(child.label));
                        const trackerFacts = tracker
                            ? Object.entries(flattenRecord(tracker.payload)).filter(([key, value]) => (
                                !columns.includes(key) && !NOISE_KEYS.has(key) && !isBlank(value)
                            ))
                            : [];
                        const canExpand = leftover.length || trackerFacts.length || nested?.error;
                        return (
                            <Fragment key={`${objectIdentity(row) || i}-${i}`}>
                                <tr
                                    onClick={() => canExpand && setExpanded((prev) => (prev === i ? null : i))}
                                    style={{ cursor: canExpand ? "pointer" : "default" }}
                                >
                                    {columns.map((key) => (
                                        <td
                                            key={key}
                                            style={{
                                                padding: ".55rem .7rem .55rem 0",
                                                borderBottom: "1px solid var(--lightestGrey)",
                                                fontSize: ".88rem",
                                                color: "var(--darkerGrey)",
                                                whiteSpace: "nowrap",
                                                textAlign: isMoneyKey(key) ? "right" : "left",
                                                fontVariantNumeric: isMoneyKey(key) ? "tabular-nums" : undefined,
                                            }}
                                        >
                                            {formatValue(key, row[key]) || "—"}
                                        </td>
                                    ))}
                                </tr>
                                {expanded === i && (
                                    <tr>
                                        <td colSpan={columns.length} style={{ padding: ".35rem 0 .9rem", borderBottom: "1px solid var(--lightestGrey)" }}>
                                            <div style={{
                                                background: "var(--lightestGrey)",
                                                borderRadius: ".5rem",
                                                padding: ".85rem 1rem",
                                            }}>
                                                {nested?.error && <p className="danger" style={{ fontSize: ".85rem", paddingBottom: leftover.length ? ".6rem" : 0 }}>{nested.error}</p>}
                                                {!!leftover.length && <FactGrid entries={leftover} />}
                                                {!!trackerFacts.length && (
                                                    <div style={{ paddingTop: leftover.length ? ".85rem" : 0 }}>
                                                        <p style={{ ...statLabelStyle, paddingBottom: ".45rem" }}>Tracker</p>
                                                        <FactGrid entries={trackerFacts} />
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        );
                    })}
                </tbody>
                {moneyCols.length >= 1 && rows.length > 1 && (
                    <tfoot>
                        <tr>
                            {columns.map((key, i) => (
                                <td
                                    key={key}
                                    style={{
                                        padding: ".6rem .7rem .2rem 0",
                                        fontSize: ".82rem",
                                        fontWeight: 700,
                                        color: "var(--darkerGrey)",
                                        textAlign: isMoneyKey(key) ? "right" : "left",
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    {i === 0 ? "Total" : (moneyCols.includes(key) ? formatValue(key, totals[key]) : "")}
                                </td>
                            ))}
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
};

const HydratedNode = ({ node, depth = 0, compact = false }) => {
    if (!node) return null;
    if (node.error && !node.payload) {
        return <p className="danger" style={{ fontSize: ".85rem" }}>{node.error}</p>;
    }

    const group = classifyViewGroup(node.label);
    const body = unwrapPayload(node.payload);
    const stats = primitiveEntries(Array.isArray(body) ? {} : body)
        .filter(([key, value]) => !isJvLink(key, value) && !isBlank(value));
    const { hero, rest } = partitionStats(stats);
    const collections = Array.isArray(body)
        ? [{ key: node.label, rows: body.filter((item) => item && typeof item === "object") }]
        : objectCollections(body);
    const { unique, groups } = splitChildNodes(node.children);
    const uniqueGrouped = groupViews(unique);
    const hideUniqueTitles = uniqueGrouped.length === 1 && uniqueGrouped[0]?.id === "other";
    const preferredColumns = COLUMNS_BY_GROUP[group.id] || CLAIM_COLUMNS;
    const hydratedTables = [...groups.entries()].map(([label, items]) => ({
        key: label,
        rows: items.map((child) => ({
            ...(child.identity ? { ClaimNumber: child.identity } : {}),
            ...flattenRecord(child.payload),
        })),
        nested: items,
    }));
    const hasHydratedClaims = hydratedTables.some(({ key }) => /claim/i.test(key));
    const collectionTables = collections
        .filter(({ key }) => !(hasHydratedClaims && /claim/i.test(key)))
        .map(({ key, rows }) => ({ key, rows, nested: null }));
    const tables = [...hydratedTables, ...collectionTables];
    const empty = !hero.length && !rest.length && !tables.length && !unique.length;

    return (
        <div>
            {depth === 0 && VIEW_BLURBS[group.id] && !compact && (
                <p style={{ color: "var(--grey)", fontSize: ".85rem", paddingBottom: hero.length || rest.length ? "1rem" : ".2rem" }}>
                    {VIEW_BLURBS[group.id]}
                </p>
            )}
            {!compact && !!hero.length && (
                <div style={{ paddingBottom: rest.length || tables.length ? "1rem" : 0 }}>
                    <KpiRow entries={hero} />
                </div>
            )}
            {!compact && !!rest.length && <FactGrid entries={rest} />}
            {empty && (
                <p style={{ color: "var(--grey)", fontSize: ".9rem" }}>No records in this view.</p>
            )}
            {tables.map(({ key, rows, nested }) => (
                <Section
                    key={key}
                    title={tableTitle(key)}
                    count={rows.length}
                    defaultOpen={depth === 0 || rows.length <= 15}
                >
                    {rows.length ? (
                        <RecordTable rows={rows} nestedByIndex={nested} preferredColumns={preferredColumns} />
                    ) : (
                        <p style={{ color: "var(--grey)", fontSize: ".85rem" }}>None.</p>
                    )}
                </Section>
            ))}
            {!!uniqueGrouped.length && depth === 0 && (
                <Section title="Related views" count={unique.length} defaultOpen={unique.length <= 2}>
                    {uniqueGrouped.map((viewGroup) => (
                        <div key={viewGroup.id} style={{ paddingBottom: ".35rem" }}>
                            {!hideUniqueTitles && uniqueGrouped.length > 1 && (
                                <p style={{ ...statLabelStyle, paddingTop: ".35rem", paddingBottom: ".25rem" }}>{viewGroup.title}</p>
                            )}
                            {viewGroup.items.map((child) => (
                                <Section
                                    key={child.href}
                                    title={hideUniqueTitles ? humanizeField(child.label) : child.displayLabel}
                                    defaultOpen={false}
                                >
                                    <HydratedNode node={child} depth={depth + 1} />
                                </Section>
                            ))}
                        </div>
                    ))}
                </Section>
            )}
            {depth === 0 && node.payload && (
                <details style={{ paddingTop: "1.2rem" }}>
                    <summary style={{ cursor: "pointer", color: "var(--grey)", fontSize: ".8rem" }}>Raw JSON</summary>
                    <ReactJson
                        style={{ paddingTop: ".6rem" }}
                        src={node.payload}
                        name={false}
                        collapsed={2}
                        collapseStringsAfterLength={150}
                        displayDataTypes={false}
                        displayObjectSize={false}
                        sortKeys={false}
                    />
                </details>
            )}
        </div>
    );
};

const ResponseFields = ({ data, onOpenLink, loadingHref, variant = "panel" }) => {
    const entries = primitiveEntries(data).filter(([key, value]) => !isJvLink(key, value) && !SPEC_STORY_KEYS.has(key));
    const { hero, rest } = partitionStats(entries);
    const { unique } = splitLinks(collectJvLinks(data));
    const dashboardRest = rest.filter(([key]) => !IDENTITY_KEY_SET.has(key) && key !== "TrackerAction");
    const hasPosition = !!(
        pickNumericField(data, PAID_KEYS)
        || pickNumericField(data, APPLIED_KEYS)
        || pickNumericField(data, DEDUCTIBLE_KEYS)
    );

    const extraFacts = [...hero, ...dashboardRest];

    if (!hasPosition && !hero.length && !rest.length && !unique.length) return null;

    return (
        <div>
            <SpecPosition row={data} />
            {!!extraFacts.length && (
                <Section title="More amounts" count={extraFacts.length} defaultOpen={false}>
                    <FactGrid entries={extraFacts} />
                </Section>
            )}
            {!!unique.length && (
                <Section title="Related views" count={unique.length} defaultOpen={false}>
                    <ViewGroups links={unique} onOpen={onOpenLink} loadingHref={loadingHref} />
                </Section>
            )}
        </div>
    );
};

export const AccumulatorPanel = ({
    rows,
    employeeId,
    certificateNumber,
    dependentId,
    effectiveDate,
    error,
    title,
    subtitle,
    variant = "panel",
    drawerZIndex = 200,
}) => {
    const [stack, setStack] = useState([]);
    const [loadingHref, setLoadingHref] = useState(null);
    const [linkError, setLinkError] = useState(null);
    const cacheRef = useRef(new Map());
    const errorsRef = useRef(new Map());

    const memberLabel = certificateNumber || employeeId;

    const openLink = async ({ label, href }) => {
        setLinkError(null);
        setLoadingHref(href);
        setStack([{ label, href, loading: true, children: [] }]);
        try {
            const node = await hydrateHref({ label, href }, {
                cache: cacheRef.current,
                errors: errorsRef.current,
                counts: { fetches: 0 },
                path: new Set(),
                depth: 0,
                rootKey: normalizeHref(href),
            });
            setStack([node]);
        } catch (err) {
            setLinkError(err?.message || "Unable to load linked record.");
            setStack([]);
        } finally {
            setLoadingHref(null);
        }
    };

    const current = stack[stack.length - 1];
    const closeDrawer = () => {
        setStack([]);
        setLinkError(null);
    };

    const heading = title || "This member";
    const memberLine = memberLabel
        ? `Paid claims vs the specific deductible for ${memberLabel}${dependentId ? `, dependent ${dependentId}` : ""}${effectiveDate ? ` as of ${effectiveDate}` : ""}.`
        : (effectiveDate ? `As of ${effectiveDate}.` : "");
    const description = subtitle || memberLine;
    const isDashboard = variant === "dashboard";

    return (
        <>
            <div style={isDashboard ? { ...cardStyle, boxShadow: "none", padding: 0 } : cardStyle}>
                <p style={{ fontSize: "1rem", fontWeight: 600, paddingBottom: ".4rem" }}>
                    {heading}
                </p>
                {description && (
                    <p style={{ color: "var(--grey)", fontSize: ".85rem", paddingBottom: "1rem" }}>
                        {description}
                    </p>
                )}
                {error && <p className="danger" style={{ paddingBottom: "1rem" }}>{error}</p>}
                {linkError && !current && <p className="danger" style={{ paddingBottom: "1rem" }}>{linkError}</p>}
                {!error && !rows.length && (
                    <p style={{ color: "var(--grey)" }}>No accumulator rows returned for this search.</p>
                )}
                {rows.map((row, i) => {
                    const entries = primitiveEntries(row);
                    const links = collectJvLinks(row);
                    if (!entries.length && !links.length) return null;
                    return (
                        <div key={`sl-accum-${i}`} style={{ paddingBottom: rows.length > 1 ? "1.2rem" : 0 }}>
                            {isDashboard && <IdentityStrip row={row} />}
                            <ResponseFields
                                data={row}
                                onOpenLink={openLink}
                                loadingHref={loadingHref}
                                variant={variant}
                            />
                        </div>
                    );
                })}
            </div>

            <Drawer
                open={!!current}
                onClose={closeDrawer}
                direction="right"
                size={DRAWER_SIZE}
                duration={200}
                zIndex={drawerZIndex}
                className="employee-stop-loss-drawer"
            >
                {current && (
                    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", background: "var(--white)", borderRadius: "inherit" }}>
                        <header style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: "1rem",
                            padding: "1rem 1.2rem",
                            borderBottom: "1px solid var(--lightGrey)",
                        }}>
                            <div style={{ minWidth: 0 }}>
                                <p className="title" style={{ fontSize: "1.15rem" }}>{humanizeField(current.label)}</p>
                                <p className="sub-title">
                                    {memberLabel ? `Employee ${memberLabel}` : ""}
                                    {memberLabel && employeeId && certificateNumber && employeeId !== certificateNumber ? ` · ${employeeId}` : ""}
                                    {dependentId ? ` · Dependent ${dependentId}` : ""}
                                </p>
                            </div>
                            <button type="button" className="btn" onClick={closeDrawer}>Close</button>
                        </header>
                        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "1rem 1.2rem 2rem" }}>
                            {linkError && <p className="danger" style={{ paddingBottom: "1rem" }}>{linkError}</p>}
                            {current.loading ? (
                                <div style={{ padding: "1.5rem 0" }}><div className="loader" /></div>
                            ) : (
                                <HydratedNode node={current} depth={0} />
                            )}
                        </div>
                    </div>
                )}
            </Drawer>
        </>
    );
};

const EmployeeStopLoss = ({ member, searchRow }) => {
    const keys = jvMemberClientKeys(member, searchRow);
    const effectiveDate = toJvDate(keys.effectiveDate);
    const canLoadContract = !!(keys.clientId && keys.blkOfBusId);
    const canLoadAccumulator = !!keys.employeeId;

    const contractQuery = useQuery({
        queryKey: ["jv-employee-stop-loss", "sl-tracker", keys.clientId, keys.blkOfBusId, keys.location, keys.department],
        queryFn: async () => {
            const result = await fetchReadJvClient({
                clientId: keys.clientId,
                blkofbusid: keys.blkOfBusId,
            });
            const client = asJvArray(result?.Response?.Client)[0] || {};
            const assignedName = assignedStopLossName(client, keys);
            const stopLoss = assignedName
                ? await fetchJvSpecStopLossContractsByNames([assignedName])
                : await fetchJvSpecStopLossContractsForClient(result);
            return { stopLoss, assignedName, client };
        },
        enabled: canLoadContract,
        staleTime: 60_000,
        retry: false,
    });

    const accumulatorQuery = useQuery({
        queryKey: ["jv-employee-sl-accumulator", "flatten-summary", keys.employeeId, keys.dependentId, effectiveDate],
        queryFn: async () => {
            const params = {
                EmployeeID: keys.employeeId,
                EffectiveDate: effectiveDate,
            };
            if (keys.dependentId) params.DependentID = keys.dependentId;
            const payload = await fetchJvSlAccumulator(params);
            return extractAccumulators(payload);
        },
        enabled: canLoadAccumulator,
        staleTime: 60_000,
        retry: false,
    });

    if (!canLoadContract && !canLoadAccumulator) {
        return (
            <p style={{ color: "var(--grey)" }}>
                No employee ID or client ID on this member, so stop-loss cannot be loaded.
            </p>
        );
    }

    return (
        <div>
            {canLoadAccumulator && (
                <>
                    {accumulatorQuery.isLoading ? (
                        <div style={{ padding: "1rem" }}><div className="loader" /></div>
                    ) : (
                        <AccumulatorPanel
                            rows={accumulatorQuery.data || []}
                            employeeId={keys.employeeId}
                            certificateNumber={keys.certificateNumber}
                            dependentId={keys.dependentId}
                            effectiveDate={effectiveDate}
                            error={accumulatorQuery.error?.message}
                        />
                    )}
                </>
            )}

            {canLoadContract && contractQuery.isLoading && (
                <div style={{ padding: "1rem" }}><div className="loader" /></div>
            )}
            {canLoadContract && contractQuery.isError && (
                <p className="danger" style={{ paddingBottom: "1rem" }}>
                    Contract: {contractQuery.error.message}
                </p>
            )}
            {contractQuery.data?.assignedName && (
                <p style={{ color: "var(--grey)", fontSize: ".85rem", padding: "0 0 .9rem" }}>
                    Contract {contractQuery.data.assignedName}
                </p>
            )}
            {contractQuery.data && (
                <StopLossContractDetail
                    stopLoss={contractQuery.data.stopLoss}
                    assignedName={contractQuery.data.assignedName}
                />
            )}
        </div>
    );
};

export default EmployeeStopLoss;
